"use client";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

interface GlobalLoadingScreenProps {
  message?: string;
}

export function GlobalLoadingScreen({ message }: GlobalLoadingScreenProps) {
  const [loadingStep, setLoadingStep] = useState(0);
  const location = useLocation();
  const pathname = location.pathname;

  // Dynamic loading steps based on the current route
  const getLoadingSteps = () => {
    if (
      pathname?.includes("/login") ||
      pathname?.includes("/verify-otp") ||
      pathname?.includes("/invite")
    ) {
      return [
        "Connecting to Shiphouse...",
        "Preparing authentication...",
        "Setting up security...",
        "Almost ready...",
      ];
    }

    if (pathname?.includes("/auth/store-selector")) {
      return [
        "Loading your stores...",
        "Fetching store data...",
        "Preparing workspace...",
        "Almost ready...",
      ];
    }

    if (pathname?.includes("/dashboard")) {
      return [
        "Loading dashboard...",
        "Fetching analytics...",
        "Preparing reports...",
        "Almost ready...",
      ];
    }

    if (pathname?.includes("/orders")) {
      return [
        "Loading orders...",
        "Fetching order data...",
        "Preparing order management...",
        "Almost ready...",
      ];
    }

    if (pathname?.includes("/products")) {
      return [
        "Loading products...",
        "Fetching inventory...",
        "Preparing catalog...",
        "Almost ready...",
      ];
    }

    if (pathname?.includes("/coupons")) {
      return [
        "Loading promotions...",
        "Fetching discount data...",
        "Preparing coupon management...",
        "Almost ready...",
      ];
    }

    if (pathname?.includes("/webbuilder")) {
      return [
        "Loading web builder...",
        "Preparing canvas...",
        "Setting up components...",
        "Almost ready...",
      ];
    }

    // Default for any other page
    return [
      "Loading Shiphouse...",
      "Preparing your workspace...",
      "Setting up interface...",
      "Almost ready...",
    ];
  };

  const loadingSteps = getLoadingSteps();

  // Dynamic message based on route
  const getDefaultMessage = () => {
    if (pathname?.includes("/login") || pathname?.includes("/verify-otp")) {
      return "Welcome to Shiphouse";
    }

    if (pathname?.includes("/auth/store-selector")) {
      return "Loading Your Stores";
    }

    if (pathname?.includes("/dashboard")) {
      return "Loading Dashboard";
    }

    if (pathname?.includes("/orders")) {
      return "Loading Orders";
    }

    if (pathname?.includes("/products")) {
      return "Loading Products";
    }

    if (pathname?.includes("/coupons")) {
      return "Loading Promotions";
    }

    if (pathname?.includes("/webbuilder")) {
      return "Loading Web Builder";
    }

    return "Loading Shiphouse";
  };

  const displayMessage = message || getDefaultMessage();

  // Cycle through loading steps
  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingStep((prev) => (prev + 1) % loadingSteps.length);
    }, 500); // Slightly slower for better readability

    return () => clearInterval(interval);
  }, [loadingSteps.length]);

  return (
    <>
      {/* Custom styles for animations */}
      <style jsx>{`
        @keyframes globalFadeInUp {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes globalSpinReverse {
          from {
            transform: rotate(360deg);
          }
          to {
            transform: rotate(0deg);
          }
        }

        @keyframes globalSlowSpin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .animate-global-spin-reverse {
          animation: globalSpinReverse 3s linear infinite;
        }

        .animate-global-slow-spin {
          animation: globalSlowSpin 4s linear infinite;
        }

        .animate-global-fade-in-up {
          animation: globalFadeInUp 0.6s ease-out;
        }

        .animate-global-fade-in-up-delay-200 {
          animation: globalFadeInUp 0.6s ease-out 0.2s both;
        }

        .animate-global-fade-in-up-delay-300 {
          animation: globalFadeInUp 0.6s ease-out 0.3s both;
        }
      `}</style>

      <div className="fixed inset-0 bg-background dark:bg-auth-background z-50 flex items-center justify-center overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-grid-pattern"></div>
        </div>

        {/* Animated Background Gradients */}
        <div className="absolute inset-0 opacity-100">
          <div className="absolute top-20 left-20 w-80 h-80 bg-primary/8 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-brand/8 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-purple-500/6 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>

        {/* Main Content */}
        <div className="relative z-10 text-center space-y-8 max-w-md mx-auto px-4">
          {/* Logo/Icon Area */}
          <div className="relative">
            {/* Spinning Rings */}
            <div className="relative w-44 h-44 mx-auto">
              <div className="absolute inset-0 border-4 border-primary/15 rounded-full"></div>
              <div className="absolute inset-4 border-4 border-primary/30 rounded-full animate-spin"></div>
              <div className="absolute inset-8 border-4 border-primary/50 rounded-full animate-global-spin-reverse"></div>
              <div className="absolute inset-12 border-4 border-primary/70 rounded-full animate-pulse"></div>
              <div className="absolute inset-16 border-2 border-brand/40 rounded-full animate-global-slow-spin"></div>

              {/* Center Logo */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 bg-gradient-to-r from-primary to-brand rounded-xl animate-pulse shadow-2xl flex items-center justify-center">
                  {/* Shiphouse Logo */}
                  <svg
                    width="352"
                    height="352"
                    viewBox="0 0 352 352"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-10 h-10"
                  >
                    <path
                      d="M21.5559 86.5221C10.5917 42.6651 43.7227 0 89.0098 0H351.198L123.571 130.141C83.766 153.023 32.7585 131.332 21.5559 86.5221Z"
                      fill="white"
                    />
                    <path
                      d="M329.754 264.809C340.719 308.666 307.587 351.331 262.3 351.331H0.112305L227.739 221.19C267.783 198.308 318.552 220.237 329.754 264.809Z"
                      fill="white"
                    />
                    <path
                      d="M175.778 219.523C199.999 219.523 219.635 199.887 219.635 175.666C219.635 151.444 199.999 131.809 175.778 131.809C151.556 131.809 131.921 151.444 131.921 175.666C131.921 199.887 151.556 219.523 175.778 219.523Z"
                      fill="white"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Main Loading Message */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-foreground min-h-[3rem] animate-global-fade-in-up bg-gradient-to-r from-foreground to-primary bg-clip-text text-transparent">
              {displayMessage}
            </h2>

            {/* Dynamic Step Message */}
            <p className="text-muted-foreground text-lg min-h-[1.75rem] animate-global-fade-in-up-delay-200">
              {loadingSteps[loadingStep]}
            </p>
          </div>

          {/* Floating Elements */}
          <div className="absolute -top-12 -left-12 w-5 h-5 bg-primary/25 rounded-full animate-bounce delay-300"></div>
          <div className="absolute -top-8 -right-10 w-4 h-4 bg-brand/25 rounded-full animate-bounce delay-700"></div>
          <div className="absolute -bottom-10 -left-8 w-3 h-3 bg-purple-500/25 rounded-full animate-bounce delay-1000"></div>
          <div className="absolute -bottom-12 -right-12 w-6 h-6 bg-green-500/25 rounded-full animate-bounce delay-500"></div>
          <div className="absolute top-1/4 -left-16 w-2 h-2 bg-blue-500/25 rounded-full animate-bounce delay-200"></div>
          <div className="absolute top-3/4 -right-16 w-2 h-2 bg-yellow-500/25 rounded-full animate-bounce delay-900"></div>

          {/* Enhanced Pulsing Dots */}
          <div className="flex justify-center space-x-3 animate-global-fade-in-up-delay-300">
            <div className="w-3 h-3 bg-gradient-to-r from-primary to-brand rounded-full animate-pulse"></div>
            <div className="w-3 h-3 bg-gradient-to-r from-brand to-primary rounded-full animate-pulse delay-300"></div>
            <div className="w-3 h-3 bg-gradient-to-r from-primary to-brand rounded-full animate-pulse delay-600"></div>
          </div>

          {/* Global Branding */}
          <div className="mt-8 text-sm text-muted-foreground/60 animate-pulse font-medium">
            Powered by Shiphouse
          </div>
        </div>

        {/* Subtle animated border */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-brand/30 to-transparent"></div>
        </div>
      </div>
    </>
  );
}

export default GlobalLoadingScreen;
